
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function VisitPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="https://static.readdy.ai/image/f322174fe77985532ed178684e9cde9f/7b841d10159b36d89f70d971de020396.png"
                alt="4 Leaf Trading"
                className="h-16 w-auto"
              />
            </Link>
            <div className="flex items-center gap-8">
              <Link to="/" className="font-medium text-gray-700 hover:text-green-600 transition-colors">
                Home
              </Link>
              <Link to="/" className="font-medium text-gray-700 hover:text-green-600 transition-colors">
                Inventory
              </Link>
              <Link to="/visit" className="font-medium text-green-600 border-b-2 border-green-600 pb-0.5 transition-colors">
                Visit Us
              </Link>
              <Link to="/admin" className="font-medium text-gray-700 hover:text-green-600 transition-colors">
                <i className="ri-settings-3-line mr-1"></i>Admin
              </Link>
              <a
                href="tel:9407365321"
                className="bg-green-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-green-700 transition-colors whitespace-nowrap"
              >
                (940) 736-5321
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Banner */}
      <section className="relative pt-20">
        <div className="relative h-64 overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/7ba2170557a13862b177214cc8310448.jpg"
            alt="4 Leaf Trading location"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white">
              <p className="text-sm font-medium tracking-widest uppercase text-green-300 mb-2">Come See Us In Person</p>
              <h1 className="text-5xl font-bold">Visit Our Shop</h1>
            </div>
          </div>
        </div>
      </section>

      {/* Address + Info Cards */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Address */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 flex flex-col items-center text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mb-4">
                <i className="ri-map-pin-2-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Our Address</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                6662 Indian Trail<br />
                Sanger, TX 76266
              </p>
              <a
                href="https://maps.google.com/?q=6662+Indian+Trail+Sanger+TX+76266"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-green-600 hover:text-green-700 transition-colors whitespace-nowrap"
              >
                Get Directions <i className="ri-external-link-line"></i>
              </a>
            </div>

            {/* Hours */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 flex flex-col items-center text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mb-4">
                <i className="ri-time-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Business Hours</h3>
              <div className="w-full space-y-2 text-sm">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">Monday – Friday</span>
                  <span className="text-gray-600">8:00 AM – 4:00 PM</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="font-medium text-gray-700">Saturday – Sunday</span>
                  <span className="text-red-500 font-semibold">Closed</span>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 flex flex-col items-center text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mb-4">
                <i className="ri-phone-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Contact Us</h3>
              <div className="space-y-3 w-full">
                <a
                  href="tel:9407365321"
                  className="flex items-center justify-center gap-2 w-full py-2.5 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors text-sm whitespace-nowrap"
                >
                  <i className="ri-phone-fill"></i>
                  (940) 736-5321
                </a>
                <a
                  href="sms:9407365321"
                  className="flex items-center justify-center gap-2 w-full py-2.5 border-2 border-green-600 text-green-600 rounded-lg font-semibold hover:bg-green-50 transition-colors text-sm whitespace-nowrap"
                >
                  <i className="ri-message-3-line"></i>
                  Send a Text
                </a>
                <a
                  href="mailto:sales@4leaftrading.com"
                  className="flex items-center justify-center gap-2 w-full py-2.5 border-2 border-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors text-sm whitespace-nowrap"
                >
                  <i className="ri-mail-line"></i>
                  sales@4leaftrading.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full Map */}
      <section className="py-4 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-200" style={{ height: '480px' }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3343.8!2d-97.17!3d33.37!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864dce3b3b3b3b3b%3A0x0!2s6662+Indian+Trail%2C+Sanger%2C+TX+76266!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="4 Leaf Trading - 6662 Indian Trail, Sanger TX"
            ></iframe>
          </div>
          <div className="mt-4 text-center">
            <a
              href="https://maps.google.com/?q=6662+Indian+Trail+Sanger+TX+76266"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors text-sm whitespace-nowrap"
            >
              <i className="ri-navigation-line"></i>
              Open in Google Maps
            </a>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">What to Expect When You Visit</h2>
            <p className="text-gray-600 max-w-xl mx-auto text-sm">
              We welcome walk-ins during business hours. Here's what your visit will look like.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: 'ri-car-line', title: 'Easy Parking', desc: 'Plenty of space for trucks and trailers on our property.' },
              { icon: 'ri-search-eye-line', title: 'Browse the Lot', desc: 'Walk the yard and inspect any tractor up close at your own pace.' },
              { icon: 'ri-steering-2-line', title: 'Test Drive', desc: 'Ask us to fire up any unit — we encourage test drives.' },
              { icon: 'ri-handshake-line', title: 'Talk to the Team', desc: 'Our knowledgeable staff will answer every question honestly.' },
            ].map((item) => (
              <div key={item.title} className="flex flex-col items-center text-center p-6 bg-green-50 rounded-2xl border border-green-100">
                <div className="w-12 h-12 flex items-center justify-center bg-green-600 rounded-full mb-4">
                  <i className={`${item.icon} text-xl text-white`}></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-green-600 to-green-700">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Browse Our Inventory?</h2>
          <p className="text-green-100 mb-8 text-sm">
            See all available tractors online before you make the drive — or just show up and we'll walk you through everything.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="px-8 py-4 bg-white text-green-700 rounded-lg font-bold hover:bg-green-50 transition-colors text-sm whitespace-nowrap"
            >
              View Inventory
            </Link>
            <a
              href="tel:9407365321"
              className="px-8 py-4 border-2 border-white text-white rounded-lg font-bold hover:bg-green-800 transition-colors text-sm whitespace-nowrap"
            >
              Call Before You Come
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-white/60">© 2025 4 Leaf Trading. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/" className="text-sm text-white/60 hover:text-white transition-colors whitespace-nowrap">Home</Link>
            <Link to="/" className="text-sm text-white/60 hover:text-white transition-colors whitespace-nowrap">Inventory</Link>
            <Link to="/visit" className="text-sm text-white/60 hover:text-white transition-colors whitespace-nowrap">Visit Us</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
